import React, { useEffect } from "react";
import { MapContainer, ImageOverlay, useMap } from "react-leaflet";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "../../store";
import { setCenter, setZoom, toggleEditMode } from "../../store/uiSlice";
import { saveGateway, fetchMapGateways } from "../../store/gatewaysSlice";
import {
  selectCurrentMap,
  selectCurrentMapView,
  setMapView,
  selectAllUserMaps,
  selectViewMode,
  saveMapPreset,
} from "../../store/mapsSlice";
import { selectViewOptions } from "../../store/userSettingsSlice";
import DeviceMarker from "./DeviceMarker";
import { calculateTooltipPositions } from "../../utils/tooltipPositioning";
import { resolveNameBoxCollisions } from "../../utils/nameBoxCollisionDetection";
import GatewayMarker from "./GatewayMarker";
import "leaflet/dist/leaflet.css";
import L from "leaflet";

// Fix for default markers in react-leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

interface MapControllerProps {
  center: [number, number];
  zoom: number;
  onMapChange: (center: [number, number], zoom: number) => void;
  mapRef?: React.MutableRefObject<L.Map | null>;
}

function MapController({
  center,
  zoom,
  onMapChange,
  mapRef,
}: MapControllerProps) {
  const map = useMap();

  // Store the map instance in the ref for external access
  React.useEffect(() => {
    if (mapRef) {
      mapRef.current = map;
    }
  }, [map]);
  const isUserInteracting = React.useRef(false);
  const updateTimeout = React.useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Only set view if user is not actively interacting
    if (!isUserInteracting.current) {
      map.setView(center, zoom);
    }
  }, [map, center, zoom]);

  useEffect(() => {
    const handleMoveStart = () => {
      isUserInteracting.current = true;
      if (updateTimeout.current) {
        clearTimeout(updateTimeout.current);
      }
    };

    const handleMoveEnd = () => {
      // Debounce the update to avoid conflicts during rapid movements
      if (updateTimeout.current) {
        clearTimeout(updateTimeout.current);
      }

      updateTimeout.current = setTimeout(() => {
        isUserInteracting.current = false;
        const newCenter = map.getCenter();
        const newZoom = map.getZoom();
        onMapChange([newCenter.lat, newCenter.lng], newZoom);
      }, 100); // 100ms debounce
    };

    map.on("movestart", handleMoveStart);
    map.on("zoomstart", handleMoveStart);
    map.on("moveend", handleMoveEnd);
    map.on("zoomend", handleMoveEnd);

    return () => {
      map.off("movestart", handleMoveStart);
      map.off("zoomstart", handleMoveStart);
      map.off("moveend", handleMoveEnd);
      map.off("zoomend", handleMoveEnd);
      if (updateTimeout.current) {
        clearTimeout(updateTimeout.current);
      }
    };
  }, [map, onMapChange]);

  return null;
}

const InteractiveMap: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const mapContainerRef = React.useRef<HTMLDivElement>(null);
  const { entities: devices } = useSelector(
    (state: RootState) => state.devices
  );
  const { entities: gateways } = useSelector(
    (state: RootState) => state.gateways
  );
  const currentMap = useSelector(selectCurrentMap);
  const currentMapView = useSelector(selectCurrentMapView);
  const allMaps = useSelector(selectAllUserMaps);
  const viewMode = useSelector(selectViewMode);

  // Debug: Log gateway changes
  React.useEffect(() => {
    // console.log(
    //   "🗺 InteractiveMap: Gateways updated:",
    //   Object.keys(gateways).length,
    //   "gateways"
    // );
    // Object.values(gateways).forEach((gateway) => {
    //   console.log(
    //     `🗺 Gateway ${gateway.mac}: viewRadius=${gateway.viewRadius}, radius=${gateway.radius}, logicThreshold=${gateway.logicThreshold}`
    //   );
    // });
  }, [gateways]);

  const { isRTL, isEditMode, isUtilityCardMinimized } = useSelector(
    (state: RootState) => state.ui
  );

  // Get view options from user settings
  const viewOptions = useSelector(selectViewOptions);
  const {
    showDeviceNames = true,
    showGatewayCoverage = false,
    showGridLines = false,
    showRSSIValues = false,
    showLastSeen = false,
    autoZoom = false,
  } = viewOptions || {};
  // Use global state as fallback when no map-specific view is saved
  const { center: globalCenter, zoom: globalZoom } = useSelector(
    (state: RootState) => state.map
  );

  // Get map-specific center and zoom from saved view state or fall back to defaults
  const mapCenter =
    currentMapView?.center ||
    (currentMap?.settings?.defaultCenter
      ? ([
          currentMap.settings.defaultCenter.x,
          currentMap.settings.defaultCenter.y,
        ] as [number, number])
      : globalCenter);
  const mapZoom =
    currentMapView?.zoom || currentMap?.settings?.defaultZoom || globalZoom;

  // Get map bounds and size from current map or use defaults
  const mapWidth = currentMap?.settings?.imageSize?.width || 800;
  const mapHeight = currentMap?.settings?.imageSize?.height || 600;

  // Calculate initial zoom to fit entire map in viewport
  const calculateFitZoom = React.useCallback(
    (containerElement?: HTMLElement) => {
      let viewportHeight: number = 400; // Default fallback
      let viewportWidth: number = 600; // Default fallback

      if (containerElement) {
        // Use actual map container dimensions - this is the most accurate method
        const containerRect = containerElement.getBoundingClientRect();
        viewportHeight = containerRect.height;
        viewportWidth = containerRect.width;
        // console.log(
        //   `📏 Using actual container dimensions: ${viewportWidth}x${viewportHeight}`
        // );
      } else {
        // Improved fallback: Try to get the map container element first
        // Use a more specific selector to target only the map card, not the utility card
        let mapCardElement = document.querySelector(
          ".bg-white.rounded-lg.shadow-lg.overflow-hidden.flex-1.flex.flex-col"
        ) as HTMLElement;
        let useMapCard = false;

        if (mapCardElement) {
          try {
            const mapRect = mapCardElement.getBoundingClientRect();
            // Account for padding inside the map card
            const cardPadding = 16; // Estimate internal padding
            const tabsHeight = allMaps.length > 0 ? 48 : 0;
            viewportHeight = Math.max(
              100,
              mapRect.height - cardPadding - tabsHeight
            ); // Minimum 100px height
            viewportWidth = Math.max(100, mapRect.width - cardPadding); // Minimum 100px width
            // console.log(
            //   `📏 Using detected map card dimensions: ${viewportWidth}x${viewportHeight} (utility card minimized: ${isUtilityCardMinimized})`
            // );
            useMapCard = true;
          } catch (error) {
            console.warn(
              "📏 Error getting map card dimensions, falling back to window calculation:",
              error
            );
            useMapCard = false;
          }
        }

        if (!useMapCard) {
          // Last resort: estimate based on window size and layout
          const headerHeight = 80; // Header height
          const padding = 32; // Total padding (16px * 2)
          const tabsHeight = allMaps.length > 0 ? 48 : 0; // Map tabs height if present

          // Fixed calculation: Only subtract utility card width when it's actually visible
          let utilityCardWidth = 0;
          if (!isUtilityCardMinimized && viewMode === "tabs") {
            // When utility card is visible, the map takes 2/3 of available width
            utilityCardWidth = window.innerWidth * 0.33 + 16; // 1/3 width + gap
          }
          // When utility card is hidden, map uses full width (lg:w-full in CSS)

          viewportHeight = Math.max(
            200,
            window.innerHeight - headerHeight - padding - tabsHeight
          ); // Minimum 200px height
          viewportWidth = Math.max(
            300,
            window.innerWidth - padding - utilityCardWidth
          ); // Minimum 300px width
          // console.log(
          //   `📏 Using fallback calculation: ${viewportWidth}x${viewportHeight} (utility card width: ${utilityCardWidth}, minimized: ${isUtilityCardMinimized})`
          // );
        }
      }

      const zoomByHeight = viewportHeight / mapHeight;
      const zoomByWidth = viewportWidth / mapWidth;

      // Use the smaller zoom to ensure entire map fits
      const rawFitZoom = Math.min(zoomByHeight, zoomByWidth, 1) * 0.9; // 0.9 for some padding

      // Ensure zoom is valid and within reasonable bounds
      const fitZoom = Math.max(0.001, Math.min(50, rawFitZoom)); // Clamp between 0.001 and 50

      // console.log(
      //   `📏 Fit zoom calculation: ${mapWidth}x${mapHeight} map → viewport ${viewportWidth}x${viewportHeight} → zoom ${fitZoom.toFixed(
      //     4
      //   )}`
      // );

      return fitZoom;
    },
    [mapHeight, mapWidth, allMaps.length, isUtilityCardMinimized, viewMode]
  );

  // Calculate dynamic zoom limits based on image size
  const calculateZoomLimits = React.useCallback(() => {
    // Minimum zoom: guarantee full image visibility with massive context
    // Make it extremely permissive - the image should appear tiny with lots of space
    const minZoom = 0.00001; // Ultra ultra permissive - image will be very tiny

    // Maximum zoom: scale generously based on image size for detail viewing
    const imageDiagonal = Math.sqrt(
      mapWidth * mapWidth + mapHeight * mapHeight
    );

    // More generous scaling - larger images get much higher zoom limits
    const baseMaxZoom = Math.max(10, imageDiagonal / 100); // More generous scaling
    const maxZoom = Math.min(100, baseMaxZoom); // Higher cap for large images

    // console.log(
    //   `🔍 Zoom limits for ${mapWidth}x${mapHeight}: min=${minZoom.toFixed(
    //     4
    //   )}, max=${maxZoom.toFixed(1)}`
    // );

    return { minZoom, maxZoom };
  }, [mapWidth, mapHeight]);

  // Handle map switching - reset view only when actually switching maps
  const previousMapId = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (currentMap && currentMap._id !== previousMapId.current) {
      // Only reset view when actually switching to a different map
      const newDefaultCenter: [number, number] = [mapHeight / 2, mapWidth / 2];
      // Try to use container element for more accurate calculation
      const containerElement = mapContainerRef.current;
      const newFitZoom = Math.max(
        0.01,
        calculateFitZoom(containerElement || undefined)
      );

      // Update global state to reflect new map's default view
      dispatch(setCenter(newDefaultCenter));
      dispatch(setZoom(newFitZoom));

      previousMapId.current = currentMap._id;
    }
  }, [currentMap, mapHeight, mapWidth, dispatch, calculateFitZoom]);

  // Image bounds for the floor plan (pixel coordinates converted to lat/lng)
  const imageBounds: [[number, number], [number, number]] = [
    [0, 0], // Southwest corner
    [mapHeight, mapWidth], // Northeast corner (height, width of the image)
  ];

  // Default center (center of the floor plan)
  const defaultCenter: [number, number] = [mapHeight / 2, mapWidth / 2];
  const center =
    mapCenter && mapCenter[0] !== 0 && mapCenter[1] !== 0
      ? mapCenter
      : defaultCenter;

  // Calculate fit zoom - try to use container element if available during render
  const calculateInitialFitZoom = React.useCallback(() => {
    const containerElement = mapContainerRef.current;
    return calculateFitZoom(containerElement || undefined);
  }, [calculateFitZoom]);

  const fitZoom = calculateInitialFitZoom();
  const { minZoom, maxZoom } = calculateZoomLimits();
  const defaultZoom = Math.max(minZoom, fitZoom);

  const handleMapChange = (newCenter: [number, number], newZoom: number) => {
    // Only update state if values have actually changed to prevent infinite loops
    const centerChanged =
      !mapCenter ||
      Math.abs(newCenter[0] - mapCenter[0]) > 0.01 ||
      Math.abs(newCenter[1] - mapCenter[1]) > 0.01;

    const zoomChanged = !mapZoom || Math.abs(newZoom - mapZoom) > 0.001;

    if (!centerChanged && !zoomChanged) {
      return; // No actual change, skip dispatch
    }

    // Update global state for backward compatibility
    dispatch(setCenter(newCenter));
    dispatch(setZoom(newZoom));

    // Save per-map view state if a map is selected
    if (currentMap?._id) {
      dispatch(
        setMapView({
          mapId: currentMap._id,
          center: newCenter,
          zoom: newZoom,
        })
      );
    }
  };

  const handleZoomIn = () => {
    const { maxZoom } = calculateZoomLimits();
    const newZoom = Math.min(maxZoom, (mapZoom || defaultZoom) + 0.2); // Bigger steps
    dispatch(setZoom(newZoom));

    // Save per-map view state if a map is selected
    if (currentMap?._id && mapCenter) {
      dispatch(
        setMapView({
          mapId: currentMap._id,
          center: mapCenter,
          zoom: newZoom,
        })
      );
    }
  };

  const handleZoomOut = () => {
    // Remove minZoom restriction completely - allow unlimited zoom out
    const currentZoom = mapZoom || defaultZoom;
    const newZoom = currentZoom - 0.2; // Bigger steps for faster zoom out

    // console.log(
    //   `🔍 Zoom OUT: ${currentZoom.toFixed(3)} → ${newZoom.toFixed(
    //     3
    //   )} (image: ${mapWidth}x${mapHeight})`
    // );

    dispatch(setZoom(newZoom));

    // Save per-map view state if a map is selected
    if (currentMap?._id && mapCenter) {
      dispatch(
        setMapView({
          mapId: currentMap._id,
          center: mapCenter,
          zoom: newZoom,
        })
      );
    }
  };

  // Add a ref to access the Leaflet map instance
  const leafletMapRef = React.useRef<L.Map | null>(null);

  // Auto-invalidate map size when utility card visibility changes
  // This ensures the map always reflects the correct container size after layout changes
  React.useEffect(() => {
    // Wait for the CSS transition to complete (300ms) before invalidating size
    const timeoutId = setTimeout(() => {
      if (leafletMapRef.current) {
        leafletMapRef.current.invalidateSize();
        console.log(
          `🗺️ Map size invalidated after utility card ${
            isUtilityCardMinimized ? "hidden" : "shown"
          }`
        );
      }
    }, 350); // Slightly longer than the 300ms CSS transition

    return () => clearTimeout(timeoutId);
  }, [isUtilityCardMinimized]);

  // Calculate tooltip positions to prevent overlapping
  const devicesWithTooltipPositions = React.useMemo(() => {
    const deviceArray = Object.values(devices).filter((device) => {
      // Only include devices that have a strongest gateway on the current map
      if (!device.strongestGateway || !currentMap) return false;
      const strongestGateway = gateways[device.strongestGateway];
      const gwMapId =
        strongestGateway &&
        typeof (strongestGateway as any).mapId === "object" &&
        (strongestGateway as any).mapId &&
        "_id" in (strongestGateway as any).mapId
          ? (strongestGateway as any).mapId._id
          : strongestGateway?.mapId;
      return strongestGateway && gwMapId === currentMap._id;
    });

    // Get map bounds for tooltip positioning
    const mapBounds = currentMap?.settings?.imageSize
      ? {
          width: currentMap.settings.imageSize.width,
          height: currentMap.settings.imageSize.height,
        }
      : undefined;

    return calculateTooltipPositions(deviceArray, mapBounds);
  }, [devices, gateways, currentMap]);

  // Apply collision detection for name boxes when enabled
  const devicesWithCollisionResolution = React.useMemo(() => {
    const showDeviceAsNameBox = viewOptions?.showDeviceAsNameBox || false;

    if (!showDeviceAsNameBox) {
      // If name boxes are not enabled, return devices as-is
      return devicesWithTooltipPositions;
    }

    // Get map dimensions for collision detection
    const mapDimensions = currentMap?.settings?.imageSize
      ? {
          width: currentMap.settings.imageSize.width,
          height: currentMap.settings.imageSize.height,
        }
      : { width: 800, height: 600 }; // Fallback only if no map dimensions available

    // Apply collision detection to reposition overlapping name boxes
    return resolveNameBoxCollisions(
      devicesWithTooltipPositions,
      gateways,
      mapDimensions
    );
  }, [devicesWithTooltipPositions, gateways, viewOptions, currentMap]);

  const handleFitToScreen = () => {
    // console.log(
    //   `🎯 Fit to screen requested (utility card minimized: ${isUtilityCardMinimized})`
    // );
    // console.log(`🗺 Map dimensions: ${mapWidth}x${mapHeight}`);
    // console.log(`🗺 Image bounds: [0,0] to [${mapHeight},${mapWidth}]`);

    // Get the Leaflet map instance
    const leafletMap = leafletMapRef.current;
    if (leafletMap) {
      // Force Leaflet to recalculate container size before fitting
      // This ensures it accounts for any recent layout changes (e.g., utility card toggle)
      leafletMap.invalidateSize();

      // Use Leaflet's built-in fitBounds method which is more reliable
      const bounds: L.LatLngBoundsExpression = [
        [0, 0], // Southwest corner
        [mapHeight, mapWidth], // Northeast corner
      ];

      console.log(`🔍 Using Leaflet fitBounds with bounds:`, bounds);

      // Fit the bounds with increased padding for better breathing room
      leafletMap.fitBounds(bounds, {
        padding: [30, 30], // Increased padding for better margins
        maxZoom: 10, // Prevent zooming in too much
      });

      // Get the calculated center and zoom from Leaflet
      const center = leafletMap.getCenter();
      const zoom = leafletMap.getZoom();

      console.log(
        `📍 Leaflet calculated center: [${center.lat.toFixed(
          2
        )}, ${center.lng.toFixed(2)}], zoom: ${zoom.toFixed(4)}`
      );

      // Update Redux state to match Leaflet's calculation
      dispatch(setCenter([center.lat, center.lng]));
      dispatch(setZoom(zoom));

      // Save per-map view state if a map is selected
      if (currentMap?._id) {
        dispatch(
          setMapView({
            mapId: currentMap._id,
            center: [center.lat, center.lng],
            zoom: zoom,
          })
        );
      }
    } else {
      console.log(
        `⚠️ No Leaflet map instance available, using improved fallback`
      );
      // Improved fallback calculation using the container element
      const containerElement = mapContainerRef.current;
      const fitZoom = calculateFitZoom(containerElement || undefined);

      console.log(`📏 Calculated fit zoom: ${fitZoom.toFixed(4)}`);

      dispatch(setCenter(defaultCenter));
      dispatch(setZoom(Math.max(0.01, fitZoom)));

      if (currentMap?._id) {
        dispatch(
          setMapView({
            mapId: currentMap._id,
            center: defaultCenter,
            zoom: Math.max(0.01, fitZoom),
          })
        );
      }
    }
  };

  const [isFullScreen, setIsFullScreen] = React.useState(false);

  const handleToggleFullScreen = () => {
    const mapContainer = mapContainerRef.current?.closest(
      ".bg-white.rounded-lg.shadow-lg"
    ); // Find the map card container

    if (!isFullScreen) {
      // Enter full screen
      if (mapContainer && mapContainer.requestFullscreen) {
        mapContainer
          .requestFullscreen()
          .then(() => {
            setIsFullScreen(true);
            console.log("🖥️ Entered fullscreen mode");
            // Force Leaflet to recalculate size after fullscreen change
            setTimeout(() => {
              if (leafletMapRef.current) {
                leafletMapRef.current.invalidateSize();
                console.log(
                  "🗺️ Map size invalidated after entering fullscreen"
                );
              }
            }, 100);
          })
          .catch((err) => {
            console.error("Failed to enter fullscreen:", err);
          });
      }
    } else {
      // Exit full screen
      if (document.exitFullscreen) {
        document
          .exitFullscreen()
          .then(() => {
            setIsFullScreen(false);
            console.log("🖥️ Exited fullscreen mode");
            // Force Leaflet to recalculate size after fullscreen change
            setTimeout(() => {
              if (leafletMapRef.current) {
                leafletMapRef.current.invalidateSize();
                console.log("🗺️ Map size invalidated after exiting fullscreen");
              }
            }, 100);
          })
          .catch((err) => {
            console.error("Failed to exit fullscreen:", err);
          });
      }
    }
  };

  // Listen for fullscreen changes (e.g., user presses ESC)
  React.useEffect(() => {
    const handleFullScreenChange = () => {
      const isCurrentlyFullScreen = !!document.fullscreenElement;
      setIsFullScreen(isCurrentlyFullScreen);

      // Force Leaflet to recalculate size after fullscreen change (e.g., ESC key)
      setTimeout(() => {
        if (leafletMapRef.current) {
          leafletMapRef.current.invalidateSize();
          console.log(
            `🗺️ Map size invalidated after fullscreen change (ESC or other): ${
              isCurrentlyFullScreen ? "entered" : "exited"
            }`
          );
        }
      }, 100);
    };

    document.addEventListener("fullscreenchange", handleFullScreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullScreenChange);
    };
  }, []);

  const handleSaveGateways = async () => {
    try {
      // Save all gateways to MongoDB
      const gatewayArray = Object.values(gateways);
      const savePromises = gatewayArray.map((gateway) =>
        dispatch(saveGateway(gateway))
      );

      await Promise.all(savePromises);
      console.log("All gateways saved successfully to MongoDB");

      // Exit edit mode after successful save
      dispatch(toggleEditMode());
    } catch (error) {
      console.error("Error saving gateways to MongoDB:", error);
      // Stay in edit mode on error to allow retry
    }
  };

  const handleToggleEditMode = async () => {
    if (isEditMode) {
      // Exiting edit mode - reset to saved positions
      if (currentMap?._id) {
        try {
          await dispatch(fetchMapGateways(currentMap._id));
          console.log("🔄 Reset gateway positions to saved configuration");
        } catch (error) {
          console.error("Failed to reset gateway positions:", error);
        }
      }
    }
    // Toggle edit mode
    dispatch(toggleEditMode());
  };

  const handleSavePreset = async () => {
    if (!currentMap?._id || !mapCenter) return;

    try {
      await dispatch(
        saveMapPreset({
          mapId: currentMap._id,
          center: mapCenter,
          zoom: mapZoom || defaultZoom,
        })
      ).unwrap();
      console.log("✅ View preset saved successfully");
      // You could add a toast notification here
    } catch (error) {
      console.error("Failed to save preset:", error);
      // You could add an error toast notification here
    }
  };

  const handleLoadPreset = () => {
    if (!currentMap?.settings?.savedView) {
      console.log("⚠️ No saved preset available for this map");
      return;
    }

    const savedView = currentMap.settings.savedView;
    const presetCenter: [number, number] = [
      savedView.center.x,
      savedView.center.y,
    ];
    const presetZoom = savedView.zoom;

    // Update the view
    dispatch(setCenter(presetCenter));
    dispatch(setZoom(presetZoom));

    // Save to local view state
    if (currentMap._id) {
      dispatch(
        setMapView({
          mapId: currentMap._id,
          center: presetCenter,
          zoom: presetZoom,
        })
      );
    }

    console.log("✅ Loaded preset view");
  };

  // Show message if no map is selected
  if (!currentMap) {
    return (
      <div
        className={`relative w-full h-full flex items-center justify-center ${
          isRTL ? "rtl" : "ltr"
        }`}
      >
        <div className="text-center text-gray-500">
          <svg
            className="mx-auto h-12 w-12 text-gray-400 mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
            />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No Map Selected
          </h3>
          <p className="text-sm text-gray-600">
            Create a new map or select an existing map to start tracking
            devices.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={mapContainerRef}
      className={`relative w-full h-full ${isRTL ? "rtl" : "ltr"}`}
    >
      <MapContainer
        center={center}
        zoom={mapZoom || defaultZoom}
        minZoom={-10}
        maxZoom={maxZoom}
        style={{ height: "100%", width: "100%" }}
        className="rounded-lg"
        crs={L.CRS.Simple}
        zoomSnap={0.001}
        zoomDelta={0.1}
      >
        {/* Render background image if available, otherwise show grid only */}
        {currentMap?.settings?.backgroundImage && (
          <ImageOverlay
            url={
              currentMap.settings.backgroundImage.startsWith("/images/")
                ? `${
                    process.env.REACT_APP_BASE_URL || "http://localhost:3051"
                  }${
                    currentMap.settings.backgroundImage
                  }?token=${localStorage.getItem("token")}`
                : currentMap.settings.backgroundImage.startsWith("/uploads/")
                ? `${
                    process.env.REACT_APP_BASE_URL || "http://localhost:3051"
                  }${currentMap.settings.backgroundImage}`
                : currentMap.settings.backgroundImage
            }
            bounds={imageBounds}
          />
        )}

        <MapController
          center={center}
          zoom={mapZoom || 1}
          onMapChange={handleMapChange}
          mapRef={leafletMapRef}
        />

        {/* Grid Lines */}
        {showGridLines && (
          <svg
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              pointerEvents: "none",
              zIndex: 1000,
            }}
          >
            {/* Vertical lines */}
            {Array.from({ length: 9 }, (_, i) => (
              <line
                key={`v-${i}`}
                x1={`${(i + 1) * 10}%`}
                y1="0%"
                x2={`${(i + 1) * 10}%`}
                y2="100%"
                stroke="rgba(0, 0, 0, 0.1)"
                strokeWidth="1"
              />
            ))}
            {/* Horizontal lines */}
            {Array.from({ length: 9 }, (_, i) => (
              <line
                key={`h-${i}`}
                x1="0%"
                y1={`${(i + 1) * 10}%`}
                x2="100%"
                y2={`${(i + 1) * 10}%`}
                stroke="rgba(0, 0, 0, 0.1)"
                strokeWidth="1"
              />
            ))}
          </svg>
        )}

        {/* Render Gateways - only show gateways belonging to current map */}
        {Object.values(gateways)
          .filter((gateway) => {
            if (!currentMap) return false;
            const gwMapId =
              typeof (gateway as any).mapId === "object" &&
              (gateway as any).mapId &&
              "_id" in (gateway as any).mapId
                ? (gateway as any).mapId._id
                : gateway.mapId;
            return gwMapId === currentMap._id;
          })
          .map((gateway) => (
            <GatewayMarker
              key={`${gateway.mac}-${gateway.viewRadius || gateway.radius}-${
                gateway.logicThreshold || gateway.maxRSSI || -50
              }`}
              gateway={gateway}
              showCoverage={showGatewayCoverage}
            />
          ))}

        {/* Render Devices with Smart Tooltip Positioning and Collision Resolution */}
        {devicesWithCollisionResolution.map((device) => (
          <DeviceMarker
            key={device.mac}
            device={device}
            showName={showDeviceNames}
            showRSSI={showRSSIValues}
            tooltipPosition={device.tooltipPosition}
          />
        ))}
      </MapContainer>

      {/* Edit Mode Indicator */}
      {isEditMode && (
        <div
          className={`absolute top-4 ${
            isRTL ? "right-4" : "left-4"
          } bg-orange-100 border-2 border-orange-500 rounded-lg shadow-lg p-3`}
          style={{ zIndex: 21 }}
        >
          <div className="flex items-center justify-between space-x-3">
            <div className="flex items-center space-x-2">
              <svg
                className="w-5 h-5 text-orange-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
              <span className="text-orange-800 font-medium text-sm">
                Edit Mode: Drag gateways to reposition
              </span>
            </div>
            <button
              onClick={handleSaveGateways}
              className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-sm font-medium transition-colors flex items-center space-x-1"
              title="Save Gateway Positions"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"
                />
              </svg>
              <span>Save</span>
            </button>
          </div>
        </div>
      )}

      {/* Map Controls */}
      <div
        className={`absolute top-4 ${
          isRTL ? "left-4" : "right-4"
        } bg-white rounded-lg shadow-lg p-2 space-y-2`}
        style={{ zIndex: 22 }}
      >
        <button
          onClick={handleZoomIn}
          className="w-8 h-8 bg-primary-600 hover:bg-primary-700 text-white rounded flex items-center justify-center transition-colors"
          title="Zoom In"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 6v6m0 0v6m0-6h6m-6 0H6"
            />
          </svg>
        </button>
        <button
          onClick={handleZoomOut}
          className="w-8 h-8 bg-primary-600 hover:bg-primary-700 text-white rounded flex items-center justify-center transition-colors"
          title="Zoom Out"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M18 12H6"
            />
          </svg>
        </button>
        <button
          onClick={handleFitToScreen}
          className="w-8 h-8 bg-green-600 hover:bg-green-700 text-white rounded flex items-center justify-center transition-colors"
          title="Fit to Screen"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 3h4a2 2 0 0 1 2 2v4m0 6v4a2 2 0 0 1-2 2h-4m-6 0H5a2 2 0 0 1-2-2v-4m0-6V5a2 2 0 0 1 2-2h4"
            />
          </svg>
        </button>
        <button
          onClick={handleToggleFullScreen}
          className={`w-8 h-8 ${
            isFullScreen
              ? "bg-red-600 hover:bg-red-700"
              : "bg-purple-600 hover:bg-purple-700"
          } text-white rounded flex items-center justify-center transition-colors`}
          title={isFullScreen ? "Exit Full Screen" : "Full Screen"}
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            {isFullScreen ? (
              // Exit fullscreen icon (compress)
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M15 15v4.5M15 15h4.5M15 15l5.25 5.25M9 15H4.5M9 15v4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25"
              />
            ) : (
              // Enter fullscreen icon (expand)
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15"
              />
            )}
          </svg>
        </button>
        <button
          onClick={handleToggleEditMode}
          className={`w-8 h-8 ${
            isEditMode
              ? "bg-orange-600 hover:bg-orange-700"
              : "bg-blue-600 hover:bg-blue-700"
          } text-white rounded flex items-center justify-center transition-colors`}
          title={isEditMode ? "Exit Edit Mode" : "Edit Mode"}
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
            />
          </svg>
        </button>
        <button
          onClick={handleSavePreset}
          className="w-8 h-8 bg-amber-600 hover:bg-amber-700 text-white rounded flex items-center justify-center transition-colors"
          title="Save View Preset"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"
            />
          </svg>
        </button>
        <button
          onClick={handleLoadPreset}
          className="w-8 h-8 bg-cyan-600 hover:bg-cyan-700 text-white rounded flex items-center justify-center transition-colors"
          title="Load View Preset"
          disabled={!currentMap?.settings?.savedView}
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
            />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default InteractiveMap;
